/**
 * 两段式文件上传（P1-C 能力）。
 *
 *   ① POST /api/storage/upload-ticket → 拿预签名 URL（暂存键）
 *   ② PUT  上传到该 URL（直传 RustFS，不经应用服务器）
 *   ③ POST /api/storage/confirm       → 服务端复核哈希/大小/MIME 后落库
 *   ④ 返回 fileObjectId，供契约提交或要素填值引用
 *
 * ⚠️ SHA-256 在前端计算仅用于**校验传输完整性**，权威值由服务端重算。
 */

import { api } from '../../api/client';

export interface UploadTicket {
  uploadUrl: string;
  objectKey: string;
  expiresAt: string;
  headers: Record<string, string>;
}

export interface ConfirmedUpload {
  fileId: string;
  objectKey: string;
  stagingKey: string | null;
  sizeBytes: number;
  detectedType: string | null;
  sha256: string;
}

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export type UploadUsage =
  | 'DELIVERABLE'
  | 'ELEMENT_ATTACHMENT'
  | 'ELEMENT_VALUE'
  | 'APPROVAL_ATTACHMENT'
  | 'PROJECT_ATTACHMENT';

export interface UploadFileOptions {
  taskId?: string;
  taskCode?: string;
  deliverableId?: string;
  /**
   * ⭐ P6.43 上传用途：决定最终对象键的 entity 前缀。
   * 不传时后端向后兼容走 `deliverable`（历史行为）。
   */
  usage?: UploadUsage;
}

export async function uploadFile(
  file: File,
  opts: UploadFileOptions = {},
): Promise<ConfirmedUpload> {
  const body = await file.arrayBuffer();
  const sha256 = await sha256Hex(body);

  const ticket = await api.post<UploadTicket>('/storage/upload-ticket', {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    taskId: opts.taskId,
    taskCode: opts.taskCode,
    deliverableId: opts.deliverableId,
    usage: opts.usage,
  });

  const putRes = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: ticket.headers,
    body,
  });
  if (!putRes.ok) {
    throw new Error(`文件直传失败（HTTP ${putRes.status}）`);
  }

  return api.post<ConfirmedUpload>('/storage/confirm', {
    objectKey: ticket.objectKey,
    sha256,
    sizeBytes: file.size,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    taskId: opts.taskId,
    taskCode: opts.taskCode,
    deliverableId: opts.deliverableId,
    usage: opts.usage,
  });
}

/** 人类可读的文件体积 */
export function fmtSize(bytes: number | null | undefined): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
